import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import postsData from "../data/blogPosts.json";
import "./blogPost.css";

const toSlug = (s = "") =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const getTagLabel = (tag) => (typeof tag === "string" ? tag : tag?.name || "");
const getTagColor = (tag) =>
  typeof tag === "string" ? "#0fa4a5" : tag?.color || "#0fa4a5";

const getSectionLabel = (section = "") => {
  const labels = {
    featured: "Featured Insight",
    parenting: "Parenting Basics",
    teaching: "Teaching Methods",
    mental: "Mental Health",
  };

  return labels[section.toLowerCase()] || "CradleAI Journal";
};

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

const getReadTime = (content = "") => {
  const words = content.replace(/[#*_`[\]()]/g, " ").trim().split(/\s+/);
  return Math.max(1, Math.ceil(words.filter(Boolean).length / 220));
};

const extractHeadings = (content = "") =>
  content
    .split("\n")
    .map((line) => {
      const match = line.match(/^(#{2,3})\s+(.+)$/);
      if (!match) return null;
      const text = match[2].replace(/\*\*/g, "").trim();
      return {
        id: toSlug(text),
        level: match[1].length,
        text,
      };
    })
    .filter(Boolean);

const scrollToHeading = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TagPill = ({ tag }) => (
  <span
    className="tag-pill"
    style={{ "--tag-color": getTagColor(tag) }}
  >
    {getTagLabel(tag)}
  </span>
);

const MarkdownHeading = ({ level, children }) => {
  const text = React.Children.toArray(children).join("");
  const id = toSlug(text);
  const Tag = `h${level}`;

  return <Tag id={id}>{children}</Tag>;
};

export const BlogPost = () => {
  const { id: slugParam } = useParams();
  const navigate = useNavigate();
  const [readProgress, setReadProgress] = useState(0);

  const post = useMemo(
    () =>
      postsData.find((p) => (p.slug || toSlug(p.title)) === slugParam) || null,
    [slugParam]
  );

  const formattedDate = formatDate(post?.date);
  const sectionLabel = getSectionLabel(post?.section);
  const readTime = getReadTime(post?.content);
  const headings = useMemo(() => extractHeadings(post?.content), [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return postsData
      .filter((item) => item.id !== post.id && item.section === post.section)
      .slice(0, 2);
  }, [post]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slugParam]);

  useEffect(() => {
    const updateProgress = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(maxScroll > 0 ? window.scrollY / maxScroll : 0);
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  const renderNav = () => (
    <nav id="menu" className="navbar navbar-default navbar-fixed-top">
      <div className="container">
        <div className="navbar-header">
          <button
            type="button"
            className="navbar-toggle collapsed"
            data-toggle="collapse"
            data-target="#bs-example-navbar-collapse-1"
          >
            <span className="sr-only">Toggle navigation</span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </button>

          <Link
            className="navbar-brand page-scroll"
            to="/"
            style={{ display: "flex", alignItems: "center" }}
          >
            <img
              src="./img/cradleai_logo.png"
              alt="CradleAI Logo"
              style={{ height: "40px", display: "inline-block" }}
            />
          </Link>
        </div>

        <div
          className="collapse navbar-collapse"
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right">
            <li>
              <Link to="/" className="page-scroll" replace>
                Home
              </Link>
            </li>
            <li>
              <Link to="/blog" className="nav-link is-active" data-router>
                Blog
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );

  if (!post) {
    return (
      <div className="blogpost">
        {renderNav()}
        <main className="missing-post">
          <p className="article-kicker">CradleAI Journal</p>
          <h1>Post not found.</h1>
          <button className="back-btn" onClick={() => navigate("/blog")}>
            Back to Blog
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="blogpost">
      {renderNav()}
      <div
        className="read-progress"
        style={{ transform: `scaleX(${readProgress})` }}
      />

      <main>
        <section className="article-hero">
          <div className="article-hero-grid">
            <div className="article-hero-copy">
              <div className="article-hero-top">
                <div className="article-breadcrumb">
                  <Link to="/blog">Journal</Link>
                  <span>{sectionLabel}</span>
                </div>

                <p className="article-kicker">{sectionLabel}</p>
              </div>

              <div className="article-hero-main">
                <h1>{post.title}</h1>
                {post.description && (
                  <p className="article-deck">{post.description}</p>
                )}
              </div>

              <div className="article-hero-footer">
                <div className="article-meta-row">
                  <div className="author-mark" aria-hidden="true">
                    C
                  </div>
                  <div>
                    <span>Written by</span>
                    <strong>{post.writtenBy || "CradleAI"}</strong>
                  </div>
                  {formattedDate && (
                    <div>
                      <span>Published</span>
                      <strong>{formattedDate}</strong>
                    </div>
                  )}
                  <div>
                    <span>Reading time</span>
                    <strong>{readTime} min read</strong>
                  </div>
                </div>

                {Array.isArray(post.tags) && post.tags.length > 0 && (
                  <div className="tags hero-tags">
                    {post.tags.map((tag, index) => (
                      <TagPill key={index} tag={tag} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <figure className="article-visual">
              <img
                src={post.image}
                alt={post.title}
                onError={(e) =>
                  (e.currentTarget.src = "./img/blog/placeholder.jpg")
                }
              />
              <figcaption>
                Evidence-informed notes for families, educators, and care
                teams.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="article-shell">
          <aside className="article-sidebar">
            <div className="sidebar-card">
              <p className="sidebar-label">In this article</p>
              <nav className="toc-list" aria-label="Article sections">
                {headings.length > 0 ? (
                  headings.map((heading) => (
                    <button
                      key={heading.id}
                      type="button"
                      className={heading.level === 3 ? "toc-nested" : ""}
                      onClick={() => scrollToHeading(heading.id)}
                    >
                      {heading.text}
                    </button>
                  ))
                ) : (
                  <span>Overview</span>
                )}
              </nav>
            </div>

            <button className="back-btn subtle" onClick={() => navigate("/blog")}>
              Back to Blog
            </button>
          </aside>

          <article className="article-paper">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => (
                  <MarkdownHeading level={2}>{children}</MarkdownHeading>
                ),
                h3: ({ children }) => (
                  <MarkdownHeading level={3}>{children}</MarkdownHeading>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </article>
        </section>

        {relatedPosts.length > 0 && (
          <section className="related-posts">
            <div className="related-heading">
              <p className="article-kicker">Read next</p>
              <h2>More from {sectionLabel}</h2>
            </div>

            <div className="related-grid">
              {relatedPosts.map((item) => {
                const slug = item.slug || toSlug(item.title);
                return (
                  <article
                    key={slug}
                    className="related-card"
                    onClick={() => navigate(`/blog/${slug}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/blog/${slug}`);
                      }
                    }}
                  >
                    <img
                      src={item.image}
                      alt=""
                      onError={(e) =>
                        (e.currentTarget.src = "./img/blog/placeholder.jpg")
                      }
                    />
                    <div>
                      <span>{formatDate(item.date)}</span>
                      <h3>{item.title}</h3>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};
